/**
 * Renders a stage's parameters, and lets a reader change the ones that can
 * actually be changed.
 *
 * Three things make this more than a definition list. A parameter carries an
 * origin, and one that cannot be reached from the pipeline YAML is marked,
 * because a reader of the YAML alone cannot discover that the file is being
 * ignored. A parameter with an `edit` block gets a field, and the rest stay
 * text — a field that took a number and then did nothing with it would be worse
 * than no field, since the reader would credit the result to their number. And
 * each group folds, because some stages carry thirty parameters and the panel is
 * narrow.
 *
 * A changed value is marked, counted at the top, and resettable, so nobody
 * starts a nine-minute run wondering whether yesterday's experiment is still in
 * effect.
 */

import { useState } from "react";

import { Button, Checkbox, Input, Select } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import {
  ORIGIN_HINT,
  ORIGIN_LABEL,
  formatParamValue,
  parseParamValue,
  type StageParam,
  type StageParamGroup,
} from "@/pipeline/params";
import { usePipelineSession } from "@/pipeline/session";

interface Props {
  groups: StageParamGroup[];
  /** Substituted into values that name a file. */
  subjectId?: string;
  /** Groups past this many start folded. */
  openFirst?: number;
  className?: string;
}

export function ParamTable({ groups, subjectId, openFirst = 2, className }: Props) {
  const overrides = usePipelineSession((s) => s.paramOverrides);
  const changedHere = groups
    .flatMap((g) => g.params)
    .filter((p) => p.edit && p.edit.key in overrides).length;

  const fill = (text: string) => text.replaceAll("{id}", subjectId || "{id}");

  return (
    <div className={cn("space-y-1", className)}>
      {changedHere > 0 ? <ChangedStrip count={changedHere} /> : null}
      {groups.map((group, index) => (
        <ParamGroup
          key={group.title}
          group={group}
          fill={fill}
          // A group holding something the reader changed opens regardless of
          // where it sits, so a changed value is never folded out of sight.
          defaultOpen={index < openFirst || group.params.some((p) => p.edit && p.edit.key in overrides)}
        />
      ))}
    </div>
  );
}

function ChangedStrip({ count }: { count: number }) {
  const reset = usePipelineSession((s) => s.resetParamOverrides);
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
      <span>
        {count} parameter{count === 1 ? "" : "s"} changed for the next run
      </span>
      <button type="button" className="shrink-0 underline" onClick={reset}>
        Reset all
      </button>
    </div>
  );
}

function ParamGroup({
  group,
  fill,
  defaultOpen,
}: {
  group: StageParamGroup;
  fill: (text: string) => string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const settable = group.params.filter((p) => p.edit).length;

  return (
    <div className="rounded border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-neutral-50"
      >
        <span className={cn("text-neutral-400 transition-transform", open && "rotate-90")}>›</span>
        <span className="flex-1 font-semibold text-neutral-700">{group.title}</span>
        <span className="text-[10px] text-neutral-400">
          {group.params.length}
          {settable > 0 ? ` · ${settable} settable` : ""}
        </span>
      </button>
      {open ? (
        <div className="border-t border-neutral-100 px-2 pb-1.5">
          {group.note ? <p className="py-1.5 text-[11px] leading-snug text-neutral-500">{group.note}</p> : null}
          {group.params.map((param) => (
            <ParamRow key={param.label} param={param} fill={fill} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ParamRow({ param, fill }: { param: StageParam; fill: (text: string) => string }) {
  return (
    <div className="border-t border-neutral-100 py-1 first:border-t-0">
      <div className="flex items-baseline justify-between gap-3" title={param.source}>
        <span className="text-neutral-500">{param.label}</span>
        {param.edit ? null : (
          <span className="text-right font-mono text-[11px] text-neutral-800">{fill(param.value)}</span>
        )}
      </div>
      {param.edit ? <ParamField param={param} /> : null}
      {param.origin !== "config" || param.note ? (
        <div className="mt-0.5 flex flex-wrap items-baseline gap-1.5">
          {param.origin !== "config" ? (
            <span
              className="rounded bg-neutral-100 px-1 py-px text-[10px] font-medium text-neutral-500"
              title={ORIGIN_HINT[param.origin]}
            >
              {ORIGIN_LABEL[param.origin]}
            </span>
          ) : null}
          {param.note ? (
            <span className="flex-1 basis-40 text-[10px] leading-snug text-neutral-400">{fill(param.note)}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One editable parameter.
 *
 * The field shows the pipeline's own value until the reader types, and the
 * override is only recorded once it parses — so a half-typed number never
 * reaches a run, and clearing the field puts the default back rather than
 * sending an empty string.
 */
function ParamField({ param }: { param: StageParam }) {
  const edit = param.edit!;
  const override = usePipelineSession((s) => s.paramOverrides[edit.key]);
  const setOverride = usePipelineSession((s) => s.setParamOverride);
  const changed = override !== undefined;

  const shown = formatParamValue(override ?? edit.fallback);
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? shown;
  const parsed = parseParamValue(edit, text);
  const error = "error" in parsed ? parsed.error : null;

  const commit = (raw: string) => {
    setDraft(raw);
    const result = parseParamValue(edit, raw);
    if ("error" in result) return;
    // Matching the default is the same as not having set it: the run carries
    // nothing, and the "changed" count stays honest.
    const same = formatParamValue(result.value) === formatParamValue(edit.fallback);
    setOverride(edit.key, same ? null : result.value);
  };

  const revert = () => {
    setDraft(null);
    setOverride(edit.key, null);
  };

  return (
    <div className="mt-1 flex items-center gap-1.5">
      {edit.kind === "boolean" ? (
        <label className="flex items-center gap-1.5 text-[11px] text-neutral-700">
          <Checkbox
            checked={text === "true"}
            onChange={(e) => commit(e.target.checked ? "true" : "false")}
          />
          {text === "true" ? "on" : "off"}
        </label>
      ) : edit.kind === "enum" ? (
        <Select className="h-6 flex-1 text-[11px]" value={text} onChange={(e) => commit(e.target.value)}>
          {edit.choices?.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          className={cn("h-6 flex-1 font-mono text-[11px]", error && "border-red-300")}
          value={text}
          inputMode={edit.kind === "number" ? "decimal" : undefined}
          placeholder={formatParamValue(edit.fallback) || "leave empty for the pipeline default"}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setDraft(null)}
        />
      )}
      {edit.unit ? <span className="shrink-0 text-[10px] text-neutral-400">{edit.unit}</span> : null}
      {changed ? (
        <Button size="sm" variant="ghost" className="h-6 shrink-0 px-1 text-[10px]" onClick={revert}>
          Reset
        </Button>
      ) : null}
      {error ? <span className="shrink-0 text-[10px] text-red-600">{error}</span> : null}
    </div>
  );
}

/** The sentence under a parameter list, worded for what the list actually is. */
export function paramsCaption(groups: StageParamGroup[]): string {
  const all = groups.flatMap((g) => g.params);
  if (all.length === 0) return "";
  const settable = all.filter((p) => p.edit).length;
  const offConfig = all.filter((p) => p.origin !== "config").length;
  if (settable === 0) {
    return `${all.length} parameters, none of them settable from here: ${offConfig} are fixed in the OpenADS runner or left at a library default. Hover a row for the file it came from.`;
  }
  return `${all.length} parameters, ${settable} settable. The other ${all.length - settable} are fixed in the OpenADS runner or left at a library default, so editing the pipeline YAML will not change them either. Hover a row for the file it came from.`;
}
